import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
// import { StrtInitiative } from './StrtInitiative';
import { IImplementationPlanInitiative } from './IImplementationPlanInitiative';
import { DatePipe } from '@angular/common';
import { ITheoryOfActionInitiative } from './ITheoryOfActionInitiative';
// import { DataUtils } from './data-utils';
import { DataUtils } from '../../shared/utilities/data-utils';
import { ActivityService } from './activity.service';
import { ICriticalInitiativeSort} from './ICriticalInitiativeSort';
import { StrtInitiative} from '../../shared/models/plan/StrtInitiative';
import {StrtInitiativeAnalysis} from '../../shared/models/plan/StrtInitiativeAnalysis';
import {StrtAssumption} from '../../shared/models/plan/StrtAssumption';
import {StrtInfluentialFactor} from '../../shared/models/plan/StrtInfluentialFactor';


@Injectable()
export class InitiativeService {
  public static readonly VBAR_BASE_COLOR = '#ccc';
  private baseUrl = environment.baseUrl;

  public static initializeImplementationPlanInitiative(implementationPlanInitiative: IImplementationPlanInitiative):
    IImplementationPlanInitiative {
    implementationPlanInitiative.headingDTO = implementationPlanInitiative.headingDTO ||
      {} as {initiativeHeading: string; keyMeasureHeading: string; objectiveHeading: string};
    implementationPlanInitiative.headingDTO.initiativeHeading = implementationPlanInitiative.headingDTO.initiativeHeading || '';
    implementationPlanInitiative.headingDTO.keyMeasureHeading = implementationPlanInitiative.headingDTO.keyMeasureHeading || '';
    implementationPlanInitiative.headingDTO.objectiveHeading = implementationPlanInitiative.headingDTO.objectiveHeading || '';
    implementationPlanInitiative.initiative = implementationPlanInitiative.initiative || '';
    implementationPlanInitiative.activities = implementationPlanInitiative.activities || [];
    return implementationPlanInitiative;
  }

  public static initializeTheoryOfActionInitiative(theoryOfActionInitiative: ITheoryOfActionInitiative): ITheoryOfActionInitiative {
    theoryOfActionInitiative.mission = theoryOfActionInitiative.mission || '';
    theoryOfActionInitiative.vision = theoryOfActionInitiative.vision || '';
    theoryOfActionInitiative.objective = theoryOfActionInitiative.objective || '';
    theoryOfActionInitiative.headingDTO = theoryOfActionInitiative.headingDTO ||
      {} as {initiativeHeading: string; keyMeasureHeading: string; objectiveHeading: string};
    theoryOfActionInitiative.headingDTO.initiativeHeading = theoryOfActionInitiative.headingDTO.initiativeHeading || 'Critical Initiatives';
    theoryOfActionInitiative.headingDTO.keyMeasureHeading = theoryOfActionInitiative.headingDTO.keyMeasureHeading || 'Key Measure(s)';
    theoryOfActionInitiative.headingDTO.objectiveHeading = theoryOfActionInitiative.headingDTO.objectiveHeading || 'Objectives';
    //  DO NOT set theoryOfActionInitiative.initiative it is used as a flag of sorts
    theoryOfActionInitiative.otherResources = theoryOfActionInitiative.otherResources || [];
    theoryOfActionInitiative.measures = theoryOfActionInitiative.measures || [];
    theoryOfActionInitiative.outcomes = theoryOfActionInitiative.outcomes || [];
    theoryOfActionInitiative.priorityColor = theoryOfActionInitiative.priorityColor || this.VBAR_BASE_COLOR;

    return theoryOfActionInitiative;
  }

  public static mapInitiativeDates(initiative: StrtInitiative) {
    DataUtils.replaceDatePropertyIfPresent(initiative, 'startDate');
    DataUtils.replaceDatePropertyIfPresent(initiative, 'endDate');
    if (initiative.activities) {
      initiative.activities.forEach(ActivityService.mapActivityDates);
    }
    return initiative;
  }

  /**
   * TODO: This is temporary function that duplicates the above but with a different type
   * This will be removed once all type refs have been updated.
   * @param {StrtInitiative} initiative
   * @returns {StrtInitiative}
   */
  public static mapInitiativeDatesAlt(initiative: StrtInitiative) {
    DataUtils.replaceDatePropertyIfPresent(initiative, 'startDate');
    DataUtils.replaceDatePropertyIfPresent(initiative, 'endDate');
    if (initiative.activities) {
      initiative.activities.forEach(ActivityService.mapActivityDates);
    }
    return initiative;
  }

  constructor(private http: HttpClient, private datePipe: DatePipe) { }

  getInitiatives(strategyId: number, startDate?: Date, endDate?: Date): Observable<StrtInitiative[]> {
    const startDateString = this.datePipe.transform(startDate, 'yyyy-MM-dd');
    const endDateString = this.datePipe.transform(endDate, 'yyyy-MM-dd');
    const requestOptions = this.createRequestOptions({});
    if (startDate) {
      requestOptions.params = requestOptions.params.set('initiativeStartDate', startDateString);
    }
    if (endDate) {
      requestOptions.params = requestOptions.params.set('initiativeEndDate', endDateString);
    }
    return this.http.get(
      `${this.baseUrl}/strategies/${strategyId}/plan/initiatives/description`, requestOptions).pipe(
      map((it: StrtInitiative[]) => it.map(InitiativeService.mapInitiativeDates)));
  }

  trackInitiative(initiative: StrtInitiative) {
    return this.http.put(
      `${this.resourcesUrl(initiative.strategyId, initiative.objectiveId)}/${initiative.initiativeId}/updatetrackingtools`,
      {isEnabled: initiative.trackingToolsEnabled});
  }

  updateInitiativeStatus(initiative: StrtInitiative) {
    return this.http.put(
      `${this.resourcesUrl(initiative.strategyId, initiative.objectiveId)}/${initiative.initiativeId}/updatestatus`,
      {initiativeStatus: initiative.initiativeStatus});
  }

  getImplementationPlanInitiative(strategyId: number, initiativeId: number): Observable<IImplementationPlanInitiative> {
    return this.http.get<IImplementationPlanInitiative>
      (`${this.baseUrl}/strategies/${strategyId}/plan/implementation-plan/initiatives/${initiativeId}`).pipe(
      map(InitiativeService.initializeImplementationPlanInitiative));
  }

  getTheoryOfActionInitiative(strategyId: number, initiativeId: number): Observable<ITheoryOfActionInitiative> {
    return this.http.get<ITheoryOfActionInitiative>
      (`${this.baseUrl}/strategies/${strategyId}/plan/theory-of-action/initiative/${initiativeId}`).pipe(
      map(InitiativeService.initializeTheoryOfActionInitiative));
  }

  createInitiative(initiative: StrtInitiative): Observable<StrtInitiative> {
    return this.http.post<StrtInitiative>(
      `${this.resourcesUrl(initiative.strategyId, initiative.objectiveId)}`, initiative).pipe(
      map(InitiativeService.mapInitiativeDatesAlt));
  }

  updateInitiative(initiative: StrtInitiative): Observable<StrtInitiative> {
    return this.http.put<StrtInitiative>(
      `${this.resourcesUrl(initiative.strategyId, initiative.objectiveId)}/${initiative.initiativeId}`, initiative).pipe(
      map(InitiativeService.mapInitiativeDatesAlt));
  }

  updateInitiativeDates(initiative: StrtInitiative): Observable<StrtInitiative> {
    return this.http.put<StrtInitiative>
    (`${this.resourcesUrl(initiative.strategyId, initiative.objectiveId)}/${initiative.initiativeId}/initiativedates`,
      {startDate: initiative.startDate, endDate: initiative.endDate}).pipe(
      map(InitiativeService.mapInitiativeDates));
  }

  deleteInitiative(initiative: StrtInitiative): Observable<{}> {
    return this.http.delete<{}>(
      `${this.resourcesUrl(initiative.strategyId, initiative.objectiveId)}/${initiative.initiativeId}`);
  }

  getHasInitiatives(strategyId: number): Observable<{hasInitiatives: boolean}> {
    return this.http.get<{hasInitiatives: boolean}>(`${this.baseUrl}/strategies/${strategyId}/plan/has-initiatives`);
  }

  private resourcesUrl(strategyId: number, objectiveId: number) {
    return `${this.baseUrl}/strategies/${strategyId}/objectives/${objectiveId}/initiatives`;
  }

  private createRequestOptions(paramsObj?: {[key: string]: string}) {
    const params: HttpParams = paramsObj
      ? Object.keys(paramsObj).reduce(
        (previous, key) => previous.set(key, paramsObj[key]),
        new HttpParams({ encoder: new HttpUrlEncodingCodec()}))
      : undefined;
    return { params };
  }

  /**
   * TODO: Test Me
   * TODO: Update response object typing and handling
   * @param {StrtInitiative} initiative
   * @returns {Observable<any>}
   */
  createCitation(strategyId: number, objectiveId: number, initiativeId: number, citation: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/strategies/${strategyId}/objectives/${objectiveId}/initiatives/${initiativeId}/citation`,
      citation);
  }

  /**
   * TODO: Test Me
   * TODO: Update response object typing and handling
   * @param {StrtInitiative} initiative
   * @returns {Observable<any>}
   */
  editCitation(strategyId: number, objectiveId: number, initiativeId: number, citation: string): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/strategies/${strategyId}/objectives/${objectiveId}/initiatives/${initiativeId}/citation`,
      citation);
  }

  /**
   * TODO: Test me
   * TODO: Update response object typing and handling
   * @param {number} strategyId
   * @param {StrtInitiative} initiative
   * @returns {Observable<any>}
   */
  deleteCitation(strategyId: number, objectiveId: number, initiativeId: number): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/strategies/${strategyId}/objectives/${objectiveId}/initiatives/${initiativeId}/citation`);
  }

  /**
   * TODO: Test Me
   * TODO: Update response object typing and handling
   * @param {ICriticalInitiativeSort} sortedInitiatives
   * @returns {Observable<any>}
   */
  sortInitiatives(sortedInitiatives: ICriticalInitiativeSort): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/strategies/${sortedInitiatives.strategyId}/objectives/${sortedInitiatives.objectiveId}/initiatives/sort`,
      sortedInitiatives);
  }

  /**
   * Will delete a status comment from a critical initiative record
   * @param {number} strategyId
   * @param {number} objectiveId
   * @param {number} initiativeId
   * @returns {Observable<any>}
   */
  deleteStatusComment(strategyId: number, objectiveId: number, initiativeId: number): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/strategies/${strategyId}/objectives/${objectiveId}/initiatives/${initiativeId}/comment`);
  }

  /**
   * Will create new status comment on a critical initiative record
   * @param {number} strategyId
   * @param {number} objectiveId
   * @param {number} initiativeId
   * @param {string} comment
   * @returns {Observable<any>}
   */
  createStatusComment(strategyId: number, objectiveId: number, initiativeId: number, comment: string): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/strategies/${strategyId}/objectives/${objectiveId}/initiatives/${initiativeId}/comment`,
      comment);
  }


  /**
   * Will edit a status comment on a critical initiative
   * @param {number} strategyId
   * @param {number} objectiveId
   * @param {number} initiativeId
   * @param {string} comment
   * @returns {Observable<any>}
   */
  editStatusComment(strategyId: number, objectiveId: number, initiativeId: number, comment: string): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/strategies/${strategyId}/objectives/${objectiveId}/initiatives/${initiativeId}/comment`,
      comment);
  }


  /**
   * Will delete an analysis from a critical initiative record
   * @param {number} strategyId
   * @param {number} objectiveId
   * @param {number} initiativeId
   * @returns {Observable<any>}
   */
  deleteInitiativeAnalysis(strategyId: number, objectiveId: number, initiativeId: number, analysisId: number): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/strategies/${strategyId}/objectives/${objectiveId}/initiatives/${initiativeId}/analysis/${analysisId}`);
  }

  /**
   * Will create new analysis on a critical initiative record
   * @param {number} strategyId
   * @param {number} objectiveId
   * @param {number} initiativeId
   * @param {string} comment
   * @returns {Observable<any>}
   */
  createInitiativeAnalysis(strategyId: number, objectiveId: number, initiativeId: number, analysis: StrtInitiativeAnalysis)
    : Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/strategies/${strategyId}/objectives/${objectiveId}/initiatives/${initiativeId}/analysis`,
      analysis.text);
  }


  /**
   * Will edit an analysis on a critical initiative
   * @param {number} strategyId
   * @param {number} objectiveId
   * @param {number} initiativeId
   * @param {string} comment
   * @returns {Observable<any>}
   */
  editInitiativeAnalysis(strategyId: number, objectiveId: number, initiativeId: number, analysis: StrtInitiativeAnalysis): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/strategies/${strategyId}/objectives/${objectiveId}/initiatives/${initiativeId}/analysis/${analysis.analysisId}`,
      analysis.text);
  }

  createAssumptions(strategyId: number, initiativeId: number, assumptions: string[]): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/strategies/${strategyId}/initiatives/${initiativeId}/assumption`,
      assumptions
    );
  }

  editAssumption(strategyId: number, initiativeId: number, assumption: StrtAssumption): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/strategies/${strategyId}/initiatives/${initiativeId}/assumption/${assumption.assumptionId}`,
      assumption.assumptionText
    );
  }

  deleteAssumption(strategyId: number, initiativeId: number, assumption: StrtAssumption): Observable<any> {
    return this.http.delete<any>(
      `${this.baseUrl}/strategies/${strategyId}/initiatives/${initiativeId}/assumption/${assumption.assumptionId}`);
  }

  createInfluentialFactors(strategyId: number, initiativeId: number, influentialFactors: string[]): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/strategies/${strategyId}/initiatives/${initiativeId}/influentialfactor`,
      influentialFactors
    );
  }

  editInfluentialFactor(strategyId: number, initiativeId: number, influentialFactor: StrtInfluentialFactor): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/strategies/${strategyId}/initiatives/${initiativeId}/influentialfactor`,
      influentialFactor
    );
  }

}
